import { DayOfWeek, LocalDate, TemporalAdjusters } from "@js-joda/core";
import { Api } from "services/api/api.service";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Modal } from "components/designV2/modal/modal";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  closePendingPdfPreview,
  openPendingPdfPreview,
  previewPdfAndDownload,
} from "utils/pdfPreview";
import {
  formatLocalDateAsApiDate,
  formatLocalDateMonthYearLabel,
  formatLocalDateRangeLabel,
  formatLocalDateWithWeekdayLabel,
} from "web-apps/company/utils/timeAndDate";
import {
  Body,
  ErrorText,
  FooterRow,
  HiddenRadio,
  RadioControl,
  RadioDescription,
  RadioGroup,
  RadioLabel,
  RadioOption,
  SectionTitle,
} from "./staffRegisterExportModal.styled";

type ExportPeriod = "day" | "week" | "month";

interface StaffRegisterExportModalProps {
  isOpen: boolean;
  selectedDate: LocalDate;
  onClose: () => void;
}

interface ExportOption {
  value: ExportPeriod;
  label: string;
  description: string;
}

const formatDayLabel = (value: LocalDate, locale: string): string =>
  formatLocalDateWithWeekdayLabel(value, locale);

const formatWeekLabel = (value: LocalDate, locale: string): string => {
  const weekStart = value.with(
    TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY),
  );
  const weekEnd = weekStart.plusDays(6);
  return formatLocalDateRangeLabel(weekStart, weekEnd, locale);
};

const formatMonthLabel = (value: LocalDate, locale: string): string =>
  formatLocalDateMonthYearLabel(value, locale);

export const StaffRegisterExportModal = ({
  isOpen,
  selectedDate,
  onClose,
}: StaffRegisterExportModalProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [companyState] = useCompanyStore();
  const [period, setPeriod] = useState<ExportPeriod>("day");
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const companyId = companyState.company?.id;

  useEffect(() => {
    if (!isOpen) return;
    setExportError(null);
  }, [isOpen]);

  const resolveRange = (): { fromDate: string; toDate: string } => {
    if (period === "day") {
      const dayKey = formatLocalDateAsApiDate(selectedDate);
      return { fromDate: dayKey, toDate: dayKey };
    }

    if (period === "week") {
      const weekStart = selectedDate.with(
        TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY),
      );
      const weekEnd = weekStart.plusDays(6);
      return {
        fromDate: formatLocalDateAsApiDate(weekStart),
        toDate: formatLocalDateAsApiDate(weekEnd),
      };
    }

    const monthStart = selectedDate.withDayOfMonth(1);
    const monthEnd = monthStart.withDayOfMonth(monthStart.lengthOfMonth());
    return {
      fromDate: formatLocalDateAsApiDate(monthStart),
      toDate: formatLocalDateAsApiDate(monthEnd),
    };
  };

  const resolveFileName = (fromDate: string, toDate: string): string => {
    if (fromDate === toDate) {
      return `staff-register-${fromDate}.pdf`;
    }
    return `staff-register-${fromDate}-${toDate}.pdf`;
  };

  const handleGeneratePdf = async (): Promise<void> => {
    if (!companyId) return;
    setExportError(null);
    setIsExporting(true);
    const pendingPreview = openPendingPdfPreview(
      t("TimeReports.StaffRegister"),
      t("TimeReports.LoadingStaffRegister"),
    );

    try {
      const range = resolveRange();
      const response = await Api().company.timeReports.exportStaffRegister(
        companyId,
        range.fromDate,
        range.toDate,
      );
      if (response.status === 204) {
        closePendingPdfPreview(pendingPreview);
        setExportError(t("TimeReports.ExportNoData"));
        return;
      }
      previewPdfAndDownload(
        pendingPreview,
        response.data,
        resolveFileName(range.fromDate, range.toDate),
      );
      onClose();
    } catch {
      closePendingPdfPreview(pendingPreview);
    } finally {
      setIsExporting(false);
    }
  };

  const options = useMemo<ExportOption[]>(
    () => [
      {
        value: "day",
        label: t("TimeReports.ExportPeriodDay"),
        description: formatDayLabel(selectedDate, i18n.language),
      },
      {
        value: "week",
        label: t("TimeReports.ExportPeriodWeek"),
        description: formatWeekLabel(selectedDate, i18n.language),
      },
      {
        value: "month",
        label: t("TimeReports.ExportPeriodMonth"),
        description: formatMonthLabel(selectedDate, i18n.language),
      },
    ],
    [i18n.language, selectedDate, t],
  );

  return (
    <Modal
      label={t("TimeReports.Export")}
      isOpen={isOpen}
      onClose={onClose}
      size="compact"
      footer={
        <FooterRow>
          <PrimaryButton
            label={t("TimeReports.GeneratePdf")}
            iconName="v2/download"
            size="small"
            onClick={handleGeneratePdf}
            disabled={!companyId}
            isLoading={isExporting}
          />
        </FooterRow>
      }
    >
      <Body>
        <SectionTitle>{t("TimeReports.ExportPeriodTitle")}</SectionTitle>
        <RadioGroup
          role="radiogroup"
          aria-label={t("TimeReports.ExportPeriodTitle")}
        >
          {options.map((option) => (
            <RadioOption key={option.value}>
              <HiddenRadio
                type="radio"
                name="staff-register-export-period"
                checked={period === option.value}
                onChange={() => setPeriod(option.value)}
              />
              <RadioControl />
              <RadioLabel>{option.label}</RadioLabel>
              <RadioDescription>{option.description}</RadioDescription>
            </RadioOption>
          ))}
        </RadioGroup>
        {exportError ? <ErrorText>{exportError}</ErrorText> : null}
      </Body>
    </Modal>
  );
};
